/**
 * Entra ID (Microsoft) sign-in for the ENTERPRISE (Azure) instance — the third
 * auth method beside email/password and Supabase SAML SSO (config/authConfig.ts).
 *
 * Inert unless the build sets VITE_AUTH_PROVIDER=entra + VITE_ENTRA_CLIENT_ID:
 * the demo/internal build never sets them, so this module is dead code there —
 * same seam pattern as isSsoLoginEnabled(). The Azure enterprise build sets:
 *
 *   VITE_AUTH_PROVIDER=entra
 *   VITE_ENTRA_CLIENT_ID=022e2df9-bfc7-4546-8df2-293b9d8f6473   (tesseract-xpm-spa)
 *   VITE_ENTRA_TENANT_ID=c0fdfbc6-f518-47f4-9e98-5517a1259553
 *   VITE_ENTRA_API_SCOPE=api://2ea27db8-74e2-4c05-8c39-8b27217901fd/access_as_user
 *
 * The access token for the API carries the user's Entra App Roles (the
 * SystemRole tier — GlobalAdmin/OrgAdmin/etc. — assigned in Entra, enforced
 * server-side by server/src/index.mjs). Portfolio-scoped roles stay ABAC data
 * in the DB. Client-side role reads here are display/UX only.
 */
import { entraAuthority } from '@/lib/azureCloud'
import { PublicClientApplication, type AccountInfo } from '@azure/msal-browser'

const env = import.meta.env as Record<string, string | undefined>

/** True on builds configured for Entra sign-in (the Azure enterprise instance). */
export function isEntraAuthEnabled(): boolean {
  return env.VITE_AUTH_PROVIDER === 'entra' && !!env.VITE_ENTRA_CLIENT_ID
}

/**
 * The tenant segment of the sign-in authority. Single tenant (the default):
 * VITE_ENTRA_TENANT_ID. With VITE_ENTRA_MULTI_TENANT=true the SPA signs in at
 * `organizations`, so people from any work or school tenant reach Microsoft
 * sign-in; the API then admits only the tenants on its allow-list
 * (entra-allowed-tenants, see docs/DIRECTORY.md).
 */
export function entraAuthorityTenant(e: Record<string, string | undefined> = env): string {
  if (/^(true|1|on|yes)$/i.test(String(e.VITE_ENTRA_MULTI_TENANT ?? '').trim())) return 'organizations'
  return e.VITE_ENTRA_TENANT_ID || 'common'
}

let msalReady: Promise<PublicClientApplication> | null = null
/** Set when this window is only a sign-in popup handing its response back. */
let popupBridgeOnly = false

function getMsal(): Promise<PublicClientApplication> {
  // A sign-in popup must not start MSAL itself: handleRedirectPromise would
  // consume the response the opener is waiting for. The popup closes itself.
  if (popupBridgeOnly) return new Promise<PublicClientApplication>(() => {})
  if (!msalReady) {
    const clientId = env.VITE_ENTRA_CLIENT_ID
    if (!clientId) throw new Error('VITE_ENTRA_CLIENT_ID is not set')
    const tenant = entraAuthorityTenant()
    const instance = new PublicClientApplication({
      auth: {
        clientId,
        authority: entraAuthority(tenant),
        redirectUri: window.location.origin,
      },
      // localStorage (not session): the redirect flow round-trips a full page
      // load, and the account must survive it and future tabs.
      cache: { cacheLocation: 'localStorage' },
    })
    // REDIRECT flow (not popup — popup monitoring is unreliable under COOP on
    // the login pages). handleRedirectPromise() runs inside this init chain,
    // which the module kicks off at IMPORT time below — before React mounts,
    // so the SPA router can't strip the auth response fragment first.
    msalReady = instance
      .initialize()
      .then(() => instance.handleRedirectPromise())
      .then((result) => {
        if (result?.account) instance.setActiveAccount(result.account)
        return instance
      })
  }
  return msalReady
}

/**
 * Attach the Entra bearer to EVERY same-origin /api/* request.
 *
 * The app has ~40 call sites that fetch its own API (S/4 lookups via
 * /api/s4/*, CLM analysis, ERP sync, admin, notifications, AI). On the Azure
 * build all of them sit behind the API's Entra gate, so a bare fetch comes
 * back 401 — which surfaces as a misleading downstream error ("SAP returned
 * HTTP 401" from odataClient's status fallback, when SAP was never reached).
 *
 * Patching fetch once here is deliberate over editing every call site: it
 * covers call sites added later, and it cannot drift. Rules:
 *   - same-origin /api/* only (MSAL's own login.microsoftonline.com traffic
 *     is cross-origin and passes through untouched, so no recursion)
 *   - never overwrite an Authorization header the caller already set
 *     (supabase-js sets its own on /api/db via the accessToken option)
 *   - any failure falls through to the original fetch
 */
function installApiBearerInterceptor(): void {
  if (typeof window === 'undefined') return
  const nativeFetch = window.fetch.bind(window)
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    try {
      const rawUrl =
        typeof input === 'string' ? input : input instanceof URL ? input.href : input.url
      const url = new URL(rawUrl, window.location.origin)
      // /api/invite/* is the invited outsider's door: it authenticates with
      // the invitation session, and must never carry a staff member's token.
      if (url.origin === window.location.origin && url.pathname.startsWith('/api/') && !url.pathname.startsWith('/api/invite/')) {
        const headers = new Headers(
          init?.headers ?? (input instanceof Request ? input.headers : undefined),
        )
        if (!headers.has('Authorization')) {
          const token = await acquireApiToken()
          if (token) {
            headers.set('Authorization', `Bearer ${token}`)
            return nativeFetch(input, { ...init, headers })
          }
        }
      }
    } catch {
      /* fall through to the unmodified request */
    }
    return nativeFetch(input, init)
  }
}

/**
 * True inside the popup window of an interactive sign-in (the electronic
 * signature re-authentication), once Microsoft has sent it back here with the
 * response. MSAL v5 needs that page to hand the response to the window that
 * opened it (redirect bridge) instead of starting the app.
 */
function isAuthPopupResponse(): boolean {
  if (typeof window === 'undefined') return false
  let opened = false
  try { opened = !!window.opener && window.opener !== window } catch { opened = false }
  if (!opened) return false
  const raw = `${window.location.hash}&${window.location.search}`
  return /[#?&](code|error)=/.test(raw) && /[#?&]state=/.test(raw)
}

// Eager bootstrap on Entra builds: start MSAL + redirect processing at module
// load. On non-Entra builds this is a no-op and the module stays inert.
if (isEntraAuthEnabled() && typeof window !== 'undefined') {
  if (isAuthPopupResponse()) {
    popupBridgeOnly = true
    void import('@azure/msal-browser/redirect-bridge')
      .then((m) => m.broadcastResponseToMainFrame())
      .catch((err) => console.error('[entra] sign-in popup could not return its response', err))
  } else {
    installApiBearerInterceptor()
    void getMsal()
  }
}

function apiScopes(): string[] {
  return env.VITE_ENTRA_API_SCOPE ? [env.VITE_ENTRA_API_SCOPE] : []
}

export async function entraGetAccount(): Promise<AccountInfo | null> {
  if (!isEntraAuthEnabled()) return null
  const msal = await getMsal()
  return msal.getActiveAccount() ?? msal.getAllAccounts()[0] ?? null
}

/** Full-page redirect to Microsoft sign-in; the promise never resolves (the
 *  page navigates away) — the session lands via handleRedirectPromise on the
 *  way back in. */
export async function entraSignIn(): Promise<never> {
  const msal = await getMsal()
  await msal.loginRedirect({ scopes: apiScopes() })
  return new Promise<never>(() => {})
}

export async function entraSignOut(): Promise<void> {
  const msal = await getMsal()
  const account = msal.getActiveAccount() ?? msal.getAllAccounts()[0]
  if (account) await msal.logoutRedirect({ account })
}

/** Access token for the XPM API — silent first, full-page redirect when
 *  interaction is unavoidable (expired session / new consent). */
export async function acquireApiToken(): Promise<string | null> {
  if (!isEntraAuthEnabled()) return null
  const msal = await getMsal()
  const account = msal.getActiveAccount() ?? msal.getAllAccounts()[0]
  if (!account) return null
  try {
    const r = await msal.acquireTokenSilent({ scopes: apiScopes(), account })
    return r.accessToken
  } catch {
    await msal.acquireTokenRedirect({ scopes: apiScopes() })
    return null
  }
}

/**
 * Sign in again, now, for an electronic signature. Opens a Microsoft sign-in
 * popup that asks for the password (or other sign-in method) even though a
 * session exists (prompt=login), for the same account. Returns the fresh
 * tokens; the server checks their auth_time, so a cached token cannot stand in
 * for a new sign-in. Call it straight from a click so the popup is allowed.
 */
export async function entraReauthenticate(): Promise<{ accessToken: string; idToken: string | null }> {
  if (!isEntraAuthEnabled()) throw new Error('Electronic signatures need Microsoft sign-in, which this deployment does not use.')
  const msal = await getMsal()
  const account = msal.getActiveAccount() ?? msal.getAllAccounts()[0]
  if (!account) throw new Error('You are not signed in.')
  const r = await msal.acquireTokenPopup({
    scopes: apiScopes(),
    account,
    loginHint: account.username,
    prompt: 'login',
  })
  if (r.account && r.account.homeAccountId !== account.homeAccountId) {
    throw new Error('You signed in as a different account. Sign with the account you are signed in with.')
  }
  return { accessToken: r.accessToken, idToken: r.idToken || null }
}

/**
 * Authorization header for API calls, empty object when Entra is off — so
 * callers can spread it unconditionally: { ...(await entraAuthHeaders()) }.
 * The data-layer phase routes the `api` data-source fetches through this.
 */
export async function entraAuthHeaders(): Promise<Record<string, string>> {
  const token = await acquireApiToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

/**
 * The user's SystemRole per the API access token's roles claim (first role
 * wins; assignment is single-role by convention). Display/UX only — the API
 * validates the same claim server-side.
 */
export async function entraSystemRole(): Promise<string> {
  const token = await acquireApiToken()
  if (!token) return 'None'
  try {
    const rawPayload = token.split('.')[1]
    if (!rawPayload) return 'None'
    const payload = rawPayload.replace(/-/g, '+').replace(/_/g, '/')
    const claims = JSON.parse(atob(payload)) as { roles?: string[] }
    return claims.roles?.[0] ?? 'None'
  } catch {
    return 'None'
  }
}
